'use client';

import Image from 'next/image';
import { useState, useCallback, useMemo } from 'react';
import { IconImage, IconNewspaper } from '@/components/ui/MonoIcons';
import { canOptimizePublicImage } from '@/lib/imageDelivery';

interface NewsImageProps {
  src?: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  imageClassName?: string;
  objectPosition?: string;
  zoom?: number; // 1..2.5, неразрушаемое приближение
  priority?: boolean;
  fill?: boolean;
  sizes?: string;
  quality?: number;
  onError?: () => void;
  onLoad?: () => void;
  unoptimized?: boolean;
  onClick?: () => void;
}

export default function NewsImage(props: NewsImageProps) {
  // Новая ссылка (в том числе версия) сбрасывает ошибку и состояние загрузки старой картинки.
  return <NewsImageContent key={props.src} {...props} />;
}

function NewsImageContent({
  src,
  alt,
  width = 800,
  height = 400,
  className = '',
  imageClassName,
  objectPosition,
  zoom,
  priority = false,
  fill = false,
  sizes,
  quality = 75,
  onError,
  onLoad,
  unoptimized,
  onClick
}: NewsImageProps) {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setIsLoading(false);
    onError?.();
  }, [onError]);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    onLoad?.();
  }, [onLoad]);

  const computedUnoptimized = unoptimized ?? !canOptimizePublicImage(src || '');
  const computedSizes = sizes ?? (fill
    ? '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
    : `${width}px`);

  const combinedImageClassName = useMemo(() => {
    const classes = [
      'transition-opacity',
      'duration-300',
      isLoading ? 'opacity-0' : 'opacity-100',
      imageClassName || 'object-cover'
    ];

    return classes.join(' ');
  }, [imageClassName, isLoading]);

  // Если нет изображения или произошла ошибка, показываем заглушку
  if (!src || imageError) {
    return (
      <div 
        className={`relative flex items-center justify-center text-site-text ${className}`}
        style={{
          background: 'var(--site-raised)',
          width: fill ? '100%' : width,
          height: fill ? '100%' : height,
        }}
      >
        <div className="text-center p-4">
          <IconNewspaper className="w-12 h-12 mx-auto mb-2 opacity-90" />
          <div className="text-sm opacity-80 max-w-[200px] truncate">
            {alt}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative ${className}`}
      style={{ width: fill ? '100%' : width, height: fill ? '100%' : height }}
      onClick={onClick}
    >
      {/* Placeholder пока загружается изображение */}
      {isLoading && (
        <div 
          className="absolute inset-0 flex items-center justify-center animate-pulse"
          style={{ background: 'var(--site-raised)' }}
        >
          <IconImage className="w-10 h-10 text-site-text opacity-90" />
        </div>
      )}
      
      <Image
        src={src}
        alt={alt}
        width={fill ? undefined : width}
        height={fill ? undefined : height}
        fill={fill}
        className={combinedImageClassName}
        style={
          objectPosition || (typeof zoom === 'number' && zoom > 1)
            ? {
                ...(objectPosition ? { objectPosition, transformOrigin: objectPosition } : {}),
                ...(typeof zoom === 'number' && zoom > 1 ? { transform: `scale(${zoom})` } : {}),
              }
            : undefined
        }
        priority={priority}
        sizes={computedSizes}
        quality={quality}
        onError={handleImageError}
        onLoad={handleImageLoad}
        placeholder="blur"
        blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
        unoptimized={computedUnoptimized}
      />
    </div>
  );
}

